import React, { useState, useRef, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import FadeIn from 'react-fade-in';
import _ from 'lodash';

import Link from '../util/link';
import Text from '../basic/Text';
import HTMLWrapper from '../basic/HTMLWrapper';
import CTASection from './components/CTASection';

import Logo from './components/Logo';
import Arrow from './components/RotatingArrow';
import HamburgerIcon from './components/HamburgerMenu';

import { HiArrowLongRight } from 'react-icons/hi2';

import MainNavMobileMenu from './components/MainNavMobileMenu';
import {
	IconLink,
	ChevronLink,
	FullFeatureLink,
	SubtextLink,
	LargeFeatureLink,
} from './components/MenuLinks';

import theme from '../../styles/theme';

const {
	colors,
	navbarHeight,
	font,
	breakPoints: { screenQuery },
} = theme;

// --- SECTIONS

const NavSectionWrapper = styled.div`
	width: 90%;
	height: 100%;
	max-width: 1100px;
	margin: 0 auto;
	display: ${({ display }) => display};
	padding: ${({ padding }) => padding};
`;

const NavSection = ({
	children,
	display,
	padding,
	className,
	onMouseLeave,
}) => {
	return (
		<NavSectionWrapper
			onMouseLeave={onMouseLeave}
			display={display}
			padding={padding}
			className={className}
		>
			{children}
		</NavSectionWrapper>
	);
};

// ------- MENU ELEMENTS

const ColumnHeaderWrapper = styled(Text)`
	margin-bottom: 16px;

	.column-header__title {
		color: #2c2d31;
		font-size: 13px;
		line-height: 16px; /* 123.077% */
		text-transform: uppercase;
		letter-spacing: 2px;
		font-family: ${font.bold};

		&.large {
			font-size: 18px;
			line-height: 24px; /* 133.333% */
			text-transform: none;
			letter-spacing: 0;
			font-family: ${font.semiBold};
			margin-bottom: 0;
		}

		svg {
			fill: #2c2d31;
			width: 30px;
			height: 30px;
			margin-left: 8px;
			transform-origin: left center;
			transform: scaleX(1);
			transition: transform 0.5s ease-in-out;
		}

		:hover {
			&.large {
				color: ${colors.brandMain};
			}
			svg {
				fill: ${colors.brandMain};
				transform: scaleX(1.3); /* Doubles the width */
			}
		}
	}
`;

const ColumnHeader = ({ menuTitleLink, menuTitle, menuTitleSubhead }) => {
	return (
		<ColumnHeaderWrapper>
			{menuTitleLink ? (
				<Link to={menuTitleLink}>
					<Text component="b5" className="column-header__title large">
						{menuTitle} <HiArrowLongRight />
					</Text>
					<HTMLWrapper
						html={menuTitleSubhead.childMarkdownRemark.html}
						className="column-header__sub-title"
					/>
				</Link>
			) : (
				<Text
					component="b5"
					className="column-header__title"
					css={{ marginBottom: 16 }}
				>
					{menuTitle}
				</Text>
			)}
		</ColumnHeaderWrapper>
	);
};

// ------- SUBMENUS

const ProductInnerWrapper = styled(NavSection)`
	position: relative;
	gap: 56px;
	grid-template-columns: auto 1fr;

	div {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
`;

const SolutionsInnerWrapper = styled(NavSection)`
	position: relative;
	grid-template-columns: repeat(4, auto);
	div {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
`;

const ResourcesInnerWrapper = styled(NavSection)`
	position: relative;
	grid-template-columns: auto 1fr;
	div {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
`;

const SubMenu = ({ subMenu, children, ref }) => {
	return (
		<>
			{/* Product: Two-column menu with accent column */}
			{subMenu === 'Products' && (
				<ProductInnerWrapper display="grid"> {children}</ProductInnerWrapper>
			)}
			{/* Solutions: Four-column menu with accent column */}
			{subMenu === 'Solutions' && (
				<SolutionsInnerWrapper display="grid">{children}</SolutionsInnerWrapper>
			)}
			{/* Resources: Two-column menu with accent column */}
			{subMenu === 'Resources' && (
				<ResourcesInnerWrapper display="grid">{children}</ResourcesInnerWrapper>
			)}
		</>
	);
};

// ------- SUBMENU COLUMNS

const SubmenuColumnWrapper = styled(FadeIn)`
	width: 100%;
	position: relative;
	padding-top: ${({ accentColumn }) => (accentColumn ? '33px' : '0px')};
	padding-bottom: ${({ accentColumn }) => (accentColumn ? '38px' : '0px')};
	margin: ${({ accentColumn }) =>
		accentColumn ? '0px 0px 0px' : '33px 0 38px'};
	border-left: ${({ borderColumn, firstColumn, accentColumn }) =>
		borderColumn && !firstColumn && !accentColumn
			? `1px solid rgba(108, 119, 136, 0.50)`
			: 'none'};
	padding-left: ${({ borderColumn, accentColumn, firstColumn }) =>
		accentColumn ? '55px' : borderColumn && !firstColumn ? '30px' : '0px'};
	padding-right: 30px;
	background-color: ${({ accentColumn }) => accentColumn && '#f0f0f0;'};

	${({ accentColumn }) =>
		accentColumn &&
		`:before {
            content: '';
            position: absolute;
            top: 0;
            left: 100%;
            width: 100vw;
            height: 100%;
            background-color: inherit;
	}`};
`;

const SubmenuColumn = ({ column, subMenu, lastColumn, firstColumn }) => {
	const accentColumn =
		subMenu === 'Products' ||
		subMenu === 'Solutions' ||
		subMenu === 'Resources';
	const borderColumn = subMenu === 'Solutions';

	return (
		<SubmenuColumnWrapper
			borderColumn={borderColumn}
			accentColumn={accentColumn && lastColumn}
			firstColumn={firstColumn}
		>
			<ColumnHeader
				menuTitleLink={column.menuTitleLink}
				menuTitle={column.menuTitle}
				menuTitleSubhead={column.menuTitleSubhead}
			/>
			{column?.menuItems?.map(menuItem => {
				if (menuItem?.linkImage && !menuItem.featureItem) {
					return (
						<IconLink
							link={menuItem.linkUrl}
							image={menuItem?.linkImage.file.url}
							header={menuItem.linkMainText}
							subheader={menuItem.linkSubTextLong}
						/>
					);
				} else if (!menuItem?.linkImage && menuItem.linkSubTextLong) {
					return (
						<SubtextLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
							subheader={menuItem.linkSubTextLong}
						/>
					);
				} else if (menuItem.featureItem && !menuItem.linkMainText) {
					return (
						<FullFeatureLink
							link={menuItem.linkUrl}
							image={menuItem?.linkImage.file.url}
							subheader={menuItem.linkSubTextLong}
						/>
					);
				} else if (menuItem.featureItem && menuItem.linkMainText) {
					return (
						<LargeFeatureLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
							image={menuItem?.linkImage.file.url}
							subheader={menuItem.linkSubTextLong}
						/>
					);
				} else {
					return (
						<ChevronLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
							subheader={menuItem.linkSubTextLong}
						/>
					);
				}
			})}
			{column.menuColumnLinkText && (
				<ChevronLink
					link={column.menuColumnUrl}
					header={column.menuColumnLinkText}
				/>
			)}
		</SubmenuColumnWrapper>
	);
};

const Container = styled.div`
	height: ${navbarHeight}px; /*no height for fixed position, need padding*/
	min-width: 100vw;

	position: sticky;
	top: 0;
	z-index: ${props =>
		props.mobileOpen ? theme.intercomZIndex + 3 : theme.navbarZIndex};

	background-color: ${colors.grayscale.white};
	box-shadow: ${theme.cardBoxShadow};
`;

const TopNavBarInner = styled(NavSection)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	position: relative;
`;

const MainMenu = styled.div`
	display: flex;
	align-items: center;
	> :first-child {
		margin-right: 24px;
	}
	height: 100%;
`;

const MainMenuItems = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;

	${screenQuery.large} {
		display: none;
	}
`;

const TopLevelLinkText = styled(Text)`
	transition-duration: ${theme.transitionDuration};
	color: ${props => (props.darkMode ? colors.white : colors.grayscale.main)};
	display: flex;
	align-items: center;

	border-bottom: 2px solid
		${props =>
			props.active && !props.calloutLink ? colors.brand.main : `transparent`};

	padding: 12px 0;
	padding-bottom: ${props => (props.active ? `2px` : `8px`)};
	margin-bottom: ${props => (props.active ? `6px` : `0px`)};

	:hover {
		cursor: pointer;
	}

	a {
		font-family: ${theme.font.semiBold};
	}
`;

const CalloutLink = styled(Link)`
	display: flex;
	background-color: #f1f1f1;
	padding: 7px 12px;
	border-radius: 5px;

	:hover {
		background-color: #333333;

		img {
			filter: invert(1);
		}
	}

	img {
		filter: ${({ darkMode }) => darkMode && `invert(1)`};
		margin-right: 5px;
		max-width: 20px;
	}
`;

const SubMenuWrapper = styled.div`
	min-width: 100vw;
	z-index: ${theme.intercomZIndex + 1};
	background: ${colors.grayscale.white};
	box-shadow: ${theme.cardBoxShadow};
	overflow: hidden;

	${screenQuery.medium} {
		display: none;
	}
`;

export default React.memo(({ darkMode, className }) => {
	const menu = useStaticQuery(graphql`
		query MainMenuQuery {
			contentfulMenu(contentful_id: { eq: "2iPT0UXwQDnf66xfYXpU9m" }) {
				...MainNavQuery
			}
		}
	`);

	const [currentIndex, setCurrentIndex] = useState(null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [mobileInView, setMobileInView] = useState(false);

	const topLevel = menu?.contentfulMenu.menuItems;

	const navRef = useRef(null);
	const subMenuRef = useRef(null);

	const resetMenu = e => {
		if (
			e.relatedTarget instanceof HTMLElement &&
			(navRef?.current.contains(e.relatedTarget) ||
				subMenuRef?.current.contains(e.relatedTarget))
		) {
			return;
		} else {
			setCurrentIndex(null);
		}
	};

	// takes .75s for mobile menu to close...
	useEffect(() => {
		if (mobileOpen) {
			setMobileInView(true);
		} else if (!mobileOpen) {
			setTimeout(() => {
				setMobileInView(false);
			}, 500);
		}
	}, [mobileOpen]);

	return (
		<Container
			key="main-menu-container"
			mobileOpen={mobileInView}
			className={`${className} main-menu-container`}
		>
			<TopNavBarInner
				onMouseLeave={resetMenu}
				className="main-menu-container-inner"
			>
				<MainMenu ref={navRef}>
					<Logo darkMode={darkMode} />

					<MainMenuItems>
						{topLevel?.map((menu, index) => {
							return (
								<React.Fragment key={`item-${menu.contentful_id}`}>
									<TopLevelLinkText
										className="navigation__top-link"
										onMouseEnter={() => setCurrentIndex(index)}
										onFocus={() => setCurrentIndex(index)}
										active={currentIndex === index}
										component="b5"
										semiBold
										tabIndex="0"
										index={index}
										calloutLink={menu.contentful_id === 'WuBkoE3T2gquta2BfyuZT'}
										darkMode={darkMode}
									>
										{menu.contentful_id === 'WuBkoE3T2gquta2BfyuZT' ? (
											<CalloutLink
												to={menu.linkUrl}
												darkMode={darkMode}
												color={darkMode ? 'white' : 'black'}
												hovercolor="white"
												className="menu__callout-link"
											>
												<img src={menu.linkImage.file.url} alt="" />
												{menu.linkMainText}
											</CalloutLink>
										) : menu.linkUrl ? (
											<Link
												to={menu.linkUrl}
												color={darkMode ? 'white' : 'black'}
												hovercolor={darkMode ? 'white' : 'black'}
											>
												{menu.linkMainText}
											</Link>
										) : (
											menu.menuTitle
										)}
										{menu.linkUrl ? null : (
											<Arrow
												className="arrow"
												darkMode={darkMode}
												rotating={currentIndex === index}
											/>
										)}
									</TopLevelLinkText>
								</React.Fragment>
							);
						})}
					</MainMenuItems>
				</MainMenu>

				<CTASection />

				<HamburgerIcon
					open={mobileOpen}
					toggle={() => setMobileOpen(!mobileOpen)}
				/>
			</TopNavBarInner>

			<SubMenuWrapper
				onMouseLeave={resetMenu}
				key="sub-menu-container"
				ref={subMenuRef}
			>
				<SubMenu subMenu={topLevel[currentIndex]?.menuTitle}>
					{topLevel[currentIndex]?.menuItems?.map((column, index) => {
						return (
							<SubmenuColumn
								column={column}
								index={index}
								currentIndex={currentIndex}
								subMenu={topLevel[currentIndex]?.menuTitle}
								lastColumn={
									index === topLevel[currentIndex]?.menuItems.length - 1
								}
								firstColumn={index === 0}
							/>
						);
					})}
				</SubMenu>
			</SubMenuWrapper>
			<MainNavMobileMenu
				key="mobile-menu"
				open={mobileOpen}
				menu={menu?.contentfulMenu.menuItems}
				toggle={() => setMobileOpen(!mobileOpen)}
			/>
		</Container>
	);
});
